import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sanitizeText } from "@/lib/sanitize";
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  ArrowUpDown,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  FileText,
  BarChart,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/components/auth-provider";
import {
  getAllLandlordLeases,
  createDirectLease,
  type CreateDirectLeaseData,
} from "@/lib/lease-service";
import { getAllUserProperties } from "@/lib/property-service";
import { Property } from "@/types/types";
import { doc, getDoc } from "firebase/firestore";
import { getFirebaseDB, getFirebaseFunctions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import moment from "moment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Form schema for adding/editing tenants
const tenantFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  property: z.string({ required_error: "Please select a property" }),
  unitId: z.string().optional(),
  leaseStart: z
    .string()
    .min(1, { message: "Please select a lease start date" }),
});

interface TenantData {
  id: string;
  name: string;
  email: string;
  phone: string;
  property: string;
  propertyId: string;
  address: string;
  rentAmount: number;
  leaseStart: string;
  leaseEnd: string;
  paymentStatus: "current" | "late" | "overdue";
  lastPayment?: string;
  nextPayment?: string;
  leaseId: string;
  image?: string;
  occupation?: string;
  employer?: string;
  emergencyContact?: string;
  notes?: string;
}

export default function TenantsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedTenant, setSelectedTenant] = useState<TenantData | null>(null);
  const [isAddTenantOpen, setIsAddTenantOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantData | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadTenants();
      loadProperties();
    }
  }, [user]);

  async function loadTenants() {
    if (!user?.id) return;

    try {
      setLoading(true);
      const db = getFirebaseDB();
      if (!db) throw new Error("Firebase not initialized");

      // Get landlord's properties and leases (using non-paginated functions)
      const [properties, leases] = await Promise.all([
        getAllUserProperties(user.id),
        getAllLandlordLeases(user.id),
      ]);

      // Filter for active leases only
      const activeLeases = leases.filter((lease) => lease.status === "active");

      // Get tenant data for each active lease
      const tenantsData: TenantData[] = await Promise.all(
        activeLeases.map(async (lease) => {
          // Get tenant user data
          const tenantRef = doc(db, "users", lease.tenantId);
          const tenantSnap = await getDoc(tenantRef);
          const tenantUser = tenantSnap.exists() ? tenantSnap.data() : null;

          // Find the property
          const property = properties.find((p) => p.id === lease.propertyId);

          // Calculate payment status
          const now = moment();
          const leaseEnd = moment(lease.endDate);
          const daysUntilEnd = leaseEnd.diff(now, "days");

          let paymentStatus: "current" | "late" | "overdue" = "current";
          // This is simplified - in a real app, you'd check actual payment records
          if (daysUntilEnd < 0) {
            paymentStatus = "overdue";
          }

          return {
            id: lease.tenantId,
            leaseId: lease.id,
            name:
              tenantUser?.name || tenantUser?.displayName || "Unknown Tenant",
            email: tenantUser?.email || "",
            phone: tenantUser?.phone || tenantUser?.phoneNumber || "",
            property: property?.title || "Unknown Property",
            propertyId: lease.propertyId,
            address: property?.address || "",
            rentAmount: lease.monthlyRent,
            leaseStart: lease.startDate,
            leaseEnd: lease.endDate,
            paymentStatus,
            lastPayment: undefined, // Would come from payment records
            nextPayment: moment(lease.startDate)
              .add(1, "month")
              .format("YYYY-MM-DD"),
            image: tenantUser?.photoURL || tenantUser?.image,
            occupation: tenantUser?.occupation,
            employer: tenantUser?.employer,
            emergencyContact: tenantUser?.emergencyContact,
            notes: "",
          };
        })
      );

      setTenants(tenantsData);
    } catch (error) {
      console.error("Error loading tenants:", error);
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }

  async function loadProperties() {
    if (!user?.id) return;

    try {
      const landlordProperties = await getAllUserProperties(user.id);
      setProperties(landlordProperties);
    } catch (error) {
      console.error("Error loading properties:", error);
      toast.error("Failed to load properties");
    }
  }

  const [viewTenant, setViewTenant] = useState<(typeof tenants)[0] | null>(
    null
  );

  // Initialize form
  const form = useForm<z.infer<typeof tenantFormSchema>>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      name: "",
      email: "",
      property: "",
      unitId: "",
      leaseStart: "",
    },
  });

  // Watch property selection to show/hide unit selector
  const selectedPropertyId = form.watch("property");

  // Calculate statistics
  const totalTenants = tenants.length;
  const currentTenants = tenants.filter(
    (t) => t.paymentStatus === "current"
  ).length;
  const lateTenants = tenants.filter((t) => t.paymentStatus === "late").length;
  const overdueTenants = tenants.filter(
    (t) => t.paymentStatus === "overdue"
  ).length;
  const onTimePaymentRate =
    totalTenants > 0 ? Math.round((currentTenants / totalTenants) * 100) : 0;
  const totalMonthlyRent = tenants.reduce((sum, tenant) => {
    // Calculate monthly rent from annual rent
    const monthlyRent = tenant.rentAmount / 12;
    return sum + monthlyRent;
  }, 0);

  // Filter and sort tenants
  const filteredTenants = tenants
    .filter((tenant) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.phone.includes(searchTerm) ||
        tenant.property.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === "all" || tenant.paymentStatus === statusFilter;

      // Property filter
      const matchesProperty =
        propertyFilter === "all" || tenant.propertyId === propertyFilter;

      return matchesSearch && matchesStatus && matchesProperty;
    })
    .sort((a, b) => {
      // Sort logic
      if (sortBy === "name") {
        return sortOrder === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === "rentAmount") {
        return sortOrder === "asc"
          ? a.rentAmount - b.rentAmount
          : b.rentAmount - a.rentAmount;
      } else if (sortBy === "leaseEnd") {
        return sortOrder === "asc"
          ? new Date(a.leaseEnd).getTime() - new Date(b.leaseEnd).getTime()
          : new Date(b.leaseEnd).getTime() - new Date(a.leaseEnd).getTime();
      } else if (sortBy === "paymentStatus") {
        const statusOrder = { current: 0, late: 1, overdue: 2 };
        return sortOrder === "asc"
          ? statusOrder[a.paymentStatus as keyof typeof statusOrder] -
              statusOrder[b.paymentStatus as keyof typeof statusOrder]
          : statusOrder[b.paymentStatus as keyof typeof statusOrder] -
              statusOrder[a.paymentStatus as keyof typeof statusOrder];
      }
      return 0;
    });

  // Handle tenant selection for editing
  const handleEditTenant = (tenant: (typeof tenants)[0]) => {
    setSelectedTenant(tenant);
    form.reset({
      name: tenant.name,
      email: tenant.email,
      phone: tenant.phone,
      property: tenant.propertyId,
      rentAmount: tenant.rentAmount,
      leaseStart: tenant.leaseStart,
      leaseEnd: tenant.leaseEnd,
      occupation: tenant.occupation || "",
      employer: tenant.employer || "",
      emergencyContact: tenant.emergencyContact || "",
      notes: tenant.notes || "",
    } as any);
  };

  // Handle tenant deletion
  const handleDeleteTenant = (tenant: (typeof tenants)[0]) => {
    setTenantToDelete(tenant);
    setIsDeleteDialogOpen(true);
  };

  // Confirm tenant deletion
  const confirmDeleteTenant = () => {
    // In a real app, this would call an API to delete the tenant
    toast("Tenant Removed", {
      description: `${tenantToDelete?.name} has been removed successfully.`,
    });
    setIsDeleteDialogOpen(false);
    setTenantToDelete(null);
  };

  // Handle form submission for adding/editing tenant
  const onSubmit = async (values: z.infer<typeof tenantFormSchema>) => {
    if (!user?.id) {
      toast.error("User not authenticated");
      return;
    }

    if (selectedTenant) {
      // Edit existing tenant (not implemented yet)
      toast("Tenant Updated", {
        description: `${values.name}'s information has been updated successfully.`,
      });
      setSelectedTenant(null);
      setIsAddTenantOpen(false);
      form.reset();
      return;
    }

    // Add new tenant
    try {
      setSubmitting(true);

      // Get selected property to check structure and validate unit
      const selectedProperty = properties.find((p) => p.id === values.property);
      if (!selectedProperty) {
        toast.error("Selected property not found");
        return;
      }

      // Validate multi-unit property has unit selected
      if (selectedProperty.structure === "multi-unit" && !values.unitId) {
        toast.error("Please select a unit for this multi-unit property");
        return;
      }

      // Validate multi-unit property has units defined
      if (
        selectedProperty.structure === "multi-unit" &&
        (!selectedProperty.units || selectedProperty.units.length === 0)
      ) {
        toast.error(
          "This property has no units defined. Please add units to the property first.",
          {
            action: {
              label: "Edit Property",
              onClick: () =>
                navigate(`/landlord/properties/${selectedProperty.id}`),
            },
          }
        );
        return;
      }

      // Get unit data if multi-unit
      let selectedUnit = null;
      if (values.unitId && selectedProperty.units) {
        selectedUnit = selectedProperty.units.find(
          (u) => u.id === values.unitId
        );
        if (!selectedUnit) {
          toast.error("Selected unit not found");
          return;
        }
        if (selectedUnit.status === "occupied") {
          toast.error("Selected unit is already occupied");
          return;
        }
      }

      // Validate lease start date
      const startDate = new Date(values.leaseStart);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        toast.error("Lease start date cannot be in the past");
        return;
      }

      // Get rent and deposit from property or unit
      const monthlyRent = selectedUnit
        ? selectedUnit.rent
        : selectedProperty.rent;
      const securityDeposit = selectedProperty.securityDeposit || monthlyRent; // Default to 1 month rent

      // Calculate lease end date from property lease duration (default 12 months)
      const leaseDurationMonths = selectedProperty.leaseDuration || 12;
      const endDate = moment(values.leaseStart)
        .add(leaseDurationMonths, "months")
        .format("YYYY-MM-DD");

      // Create lease data
      const leaseData: CreateDirectLeaseData = {
        tenantEmail: values.email,
        tenantName: sanitizeText(values.name), // Sanitize tenant name
        tenantPhone: "", // Will be filled by tenant during profile setup
        propertyId: values.property,
        unitId: values.unitId || undefined,
        monthlyRent,
        securityDeposit,
        startDate: values.leaseStart,
        endDate,
      };

      // Create the lease
      await createDirectLease(leaseData, user.id);

      // Send invitation email via Cloud Function
      try {
        const functions = getFirebaseFunctions();
        if (!functions) {
          throw new Error("Firebase functions not initialized");
        }
        const inviteTenant = httpsCallable(functions, "inviteTenant");

        await inviteTenant({
          email: values.email,
          name: values.name,
          propertyId: values.property,
          propertyName: selectedProperty.title,
          unitId: values.unitId,
          role: "tenant",
        });

        toast.success("Tenant Added & Invited", {
          description: `${values.name} has been added and an invitation email has been sent to ${values.email}.`,
        });
      } catch (emailError) {
        console.error("Error sending invitation:", emailError);
        // Don't fail the whole operation if email fails, but warn the user
        toast.warning("Tenant Added, but Email Failed", {
          description: `${values.name} was added, but we couldn't send the invitation email. Please contact support.`,
        });
      }

      // Reset form and close dialog
      form.reset();
      setSelectedTenant(null);
      setIsAddTenantOpen(false);

      // Reload tenants list
      await loadTenants();
    } catch (error: any) {
      console.error("Error adding tenant:", error);
      toast.error("Failed to add tenant", {
        description: error.message || "Please try again later",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when opening add tenant dialog
  const handleAddTenantClick = () => {
    form.reset({
      name: "",
      email: "",
      property: "",
      unitId: "",
      leaseStart: "",
    });
    setSelectedTenant(null);
    setIsAddTenantOpen(true);
  };

  // Format currency in Naira
  const formatNaira = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get payment status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "current":
        return <Badge className="bg-green-500">Current</Badge>;
      case "late":
        return <Badge className="bg-yellow-500">Late</Badge>;
      case "overdue":
        return <Badge className="bg-red-500">Overdue</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Calculate days until lease expiration
  const getDaysUntilLeaseEnd = (leaseEndDate: string) => {
    const today = new Date();
    const leaseEnd = new Date(leaseEndDate);
    const diffTime = leaseEnd.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTenants}</div>
            <p className="text-xs text-muted-foreground">
              Across {new Set(tenants.map((t) => t.propertyId)).size} properties
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              On-Time Payment Rate
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{onTimePaymentRate}%</div>
            <p className="text-xs text-muted-foreground">
              {currentTenants} current, {lateTenants} late, {overdueTenants}{" "}
              overdue
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Monthly Rent Revenue
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNaira(totalMonthlyRent)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average: {formatNaira(totalMonthlyRent / totalTenants)}/month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Expiring Leases
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {
                tenants.filter((t) => getDaysUntilLeaseEnd(t.leaseEnd) <= 30)
                  .length
              }
            </div>
            <p className="text-xs text-muted-foreground">
              Expiring in the next 30 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Management Tools */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tenants..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="late">Late</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>

            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {tenants
                  .reduce(
                    (properties: { id: string; name: string }[], tenant) => {
                      if (!properties.find((p) => p.id === tenant.propertyId)) {
                        properties.push({
                          id: tenant.propertyId,
                          name: tenant.property,
                        });
                      }
                      return properties;
                    },
                    []
                  )
                  .map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Download className="mr-2 h-4 w-4" /> Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Download className="mr-2 h-4 w-4" /> Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" onClick={handleAddTenantClick}>
            <Plus className="mr-2 h-4 w-4" />
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Tenants</CardTitle>
          <CardDescription>
            Manage and monitor all your tenants in one place. Showing{" "}
            {filteredTenants.length} of {tenants.length} tenants.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("name");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Tenant
                    {sortBy === "name" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>Property</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("rentAmount");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Monthly Rent
                    {sortBy === "rentAmount" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("paymentStatus");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Payment Status
                    {sortBy === "paymentStatus" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead>Next Payment</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    className="p-0 font-medium flex items-center"
                    onClick={() => {
                      setSortBy("leaseEnd");
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    }}
                  >
                    Lease End
                    {sortBy === "leaseEnd" && (
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No tenants found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={tenant.image} alt={tenant.name} />
                          <AvatarFallback>
                            {tenant.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {tenant.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{tenant.property}</TableCell>
                    <TableCell>
                      {formatNaira(tenant.rentAmount / 12)}/mo
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(tenant.paymentStatus)}
                    </TableCell>
                    <TableCell>{tenant.nextPayment}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{tenant.leaseEnd}</span>
                        {getDaysUntilLeaseEnd(tenant.leaseEnd) <= 30 && (
                          <span className="text-xs text-red-500">
                            {getDaysUntilLeaseEnd(tenant.leaseEnd)} days left
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => setViewTenant(tenant)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleEditTenant(tenant)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Tenant
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/dashboard/tenants/${tenant.id}/payments`
                              )
                            }
                          >
                            <CreditCard className="mr-2 h-4 w-4" />
                            Payment History
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/dashboard/tenants/${tenant.id}/documents`
                              )
                            }
                          >
                            <FileText className="mr-2 h-4 w-4" />
                            Documents
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteTenant(tenant)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove Tenant
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="flex justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {filteredTenants.length} of {tenants.length} tenants
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </CardFooter>
      </Card>

      {/* Tenant Analytics */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Analytics</CardTitle>
          <CardDescription>
            Key metrics and performance indicators for your tenant
            relationships.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="payments">
            <TabsList className="mb-4">
              <TabsTrigger value="payments">Payment Trends</TabsTrigger>
              <TabsTrigger value="leases">Lease Expirations</TabsTrigger>
              <TabsTrigger value="retention">Tenant Retention</TabsTrigger>
            </TabsList>
            <TabsContent value="payments" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">Payment Trends Chart</p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    payment trends)
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="leases" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    Lease Expiration Chart
                  </p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    lease expirations)
                  </p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="retention" className="space-y-4">
              <div className="h-[300px] bg-muted/20 rounded-md flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    Tenant Retention Chart
                  </p>
                  <p className="text-sm text-muted-foreground">
                    (In a real app, this would be an interactive chart showing
                    tenant retention rates)
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add/Edit Tenant Dialog */}
      <Dialog
        open={isAddTenantOpen || selectedTenant !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddTenantOpen(false);
            setSelectedTenant(null);
          }
        }}
      >
        <DialogContent className="max-w-7xl! max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedTenant ? "Edit Tenant" : "Add New Tenant"}
            </DialogTitle>
            <DialogDescription>
              {selectedTenant
                ? "Update the details of your tenant."
                : "Fill in the details to add a new tenant to your system."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col overflow-hidden"
            >
              <div className="overflow-y-auto px-1 space-y-6 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter tenant's full name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter tenant's email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-6 pt-6 border-t">
                  <FormField
                    control={form.control}
                    name="property"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Property *</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value === "none" ? "" : value);
                            // Reset unit selection when property changes
                            form.setValue("unitId", "");
                          }}
                          value={field.value || "none"}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select property" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {properties.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">
                                No properties available
                              </div>
                            ) : (
                              properties.map((property) => (
                                <SelectItem
                                  key={property.id}
                                  value={property.id}
                                >
                                  {property.title} - {property.address}
                                  {property.structure === "multi-unit" &&
                                    " (Multi-unit)"}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Select the property for this lease
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Conditional Unit Dropdown for Multi-Unit Properties */}
                  {selectedPropertyId &&
                    properties.find((p) => p.id === selectedPropertyId)
                      ?.structure === "multi-unit" && (
                      <FormField
                        control={form.control}
                        name="unitId"
                        render={({ field }) => {
                          const selectedProperty = properties.find(
                            (p) => p.id === selectedPropertyId
                          );
                          const availableUnits =
                            selectedProperty?.units?.filter(
                              (unit) => unit.status !== "occupied"
                            ) || [];

                          return (
                            <FormItem>
                              <FormLabel>Unit *</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a unit" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {availableUnits.length === 0 ? (
                                    <div className="p-2 text-sm text-muted-foreground">
                                      No available units for this property
                                    </div>
                                  ) : (
                                    availableUnits
                                      .sort((a, b) => {
                                        if (a.floor !== b.floor)
                                          return a.floor - b.floor;
                                        return a.name.localeCompare(b.name);
                                      })
                                      .map((unit) => (
                                        <SelectItem
                                          key={unit.id}
                                          value={unit.id}
                                        >
                                          {unit.name} - ₦
                                          {unit.rent.toLocaleString()}/mo
                                          {unit.floor > 0 &&
                                            ` (Floor ${unit.floor})`}
                                        </SelectItem>
                                      ))
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    )}
                </div>

                <div className="pt-6 border-t">
                  <FormField
                    control={form.control}
                    name="leaseStart"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lease Start Date *</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormDescription>
                          {selectedPropertyId &&
                            properties.length > 0 &&
                            (() => {
                              const property = properties.find(
                                (p) => p.id === selectedPropertyId
                              );
                              const duration = property?.leaseDuration || 12;
                              return `Lease will be for ${duration} month${
                                duration !== 1 ? "s" : ""
                              } as per property terms`;
                            })()}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6 pt-4 border-t">
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {selectedTenant ? "Updating..." : "Adding Tenant..."}
                    </>
                  ) : (
                    <>{selectedTenant ? "Update Tenant" : "Add Tenant"}</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Removal</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove &quot;{tenantToDelete?.name}&quot;
              from your tenants? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTenant}>
              Remove Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Tenant Details Dialog */}
      <Dialog
        open={viewTenant !== null}
        onOpenChange={(open) => !open && setViewTenant(null)}
      >
        <DialogContent className="max-w-3xl">
          {viewTenant && (
            <>
              <DialogHeader>
                <DialogTitle>Tenant Details</DialogTitle>
                <DialogDescription>
                  Comprehensive information about {viewTenant.name}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1 flex flex-col items-center">
                  <Avatar className="h-24 w-24 mb-4">
                    <AvatarImage src={viewTenant.image} alt={viewTenant.name} />
                    <AvatarFallback className="text-2xl">
                      {viewTenant.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>

                  <h3 className="text-xl font-semibold text-center">
                    {viewTenant.name}
                  </h3>
                  <p className="text-muted-foreground text-center mb-4">
                    {viewTenant.occupation} at {viewTenant.employer}
                  </p>

                  <div className="w-full space-y-2">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm">{viewTenant.email}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm">{viewTenant.phone}</span>
                    </div>
                    <div className="flex items-start">
                      <User className="h-4 w-4 mr-2 text-muted-foreground mt-1" />
                      <span className="text-sm">
                        {viewTenant.emergencyContact}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 space-y-6">
                  <div>
                    <h4 className="text-sm font-medium mb-2">
                      Property Information
                    </h4>
                    <div className="bg-muted p-3 rounded-md">
                      <p className="font-medium">{viewTenant.property}</p>
                      <p className="text-sm text-muted-foreground">
                        {viewTenant.address}
                      </p>
                      <div className="mt-2 flex justify-between">
                        <span className="text-sm">
                          Monthly Rent:{" "}
                          <span className="font-medium">
                            {formatNaira(viewTenant.rentAmount / 12)}
                          </span>
                        </span>
                        <span className="text-sm">
                          Annual Rent:{" "}
                          <span className="font-medium">
                            {formatNaira(viewTenant.rentAmount)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium mb-2">
                      Lease Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-muted p-3 rounded-md">
                        <p className="text-sm text-muted-foreground">
                          Lease Start
                        </p>
                        <p className="font-medium">{viewTenant.leaseStart}</p>
                      </div>
                      <div className="bg-muted p-3 rounded-md">
                        <p className="text-sm text-muted-foreground">
                          Lease End
                        </p>
                        <p className="font-medium">{viewTenant.leaseEnd}</p>
                        {getDaysUntilLeaseEnd(viewTenant.leaseEnd) <= 30 && (
                          <p className="text-xs text-red-500 mt-1">
                            {getDaysUntilLeaseEnd(viewTenant.leaseEnd)} days
                            remaining
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium mb-2">Payment Status</h4>
                    <div className="bg-muted p-3 rounded-md">
                      <div className="flex justify-between items-center mb-2">
                        <span>
                          Current Status:{" "}
                          {getStatusBadge(viewTenant.paymentStatus)}
                        </span>
                        <span className="text-sm">
                          Next Payment:{" "}
                          <span className="font-medium">
                            {viewTenant.nextPayment}
                          </span>
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground mb-1">
                          Recent Payment History
                        </p>
                        {(viewTenant as any).paymentHistory
                          ?.slice(0, 3)
                          .map((payment: any, index: number) => (
                            <div
                              key={index}
                              className="flex justify-between items-center text-sm"
                            >
                              <span>{payment.date}</span>
                              <span>{formatNaira(payment.amount)}</span>
                              <Badge
                                className={
                                  payment.status === "paid"
                                    ? "bg-green-500"
                                    : payment.status === "late"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                }
                              >
                                {payment.status.charAt(0).toUpperCase() +
                                  payment.status.slice(1)}
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium mb-2">Documents</h4>
                    <div className="bg-muted p-3 rounded-md">
                      <div className="space-y-2">
                        {(viewTenant as any).documents?.map(
                          (doc: any, index: number) => (
                            <div
                              key={index}
                              className="flex justify-between items-center"
                            >
                              <div className="flex items-center">
                                <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                                <span className="text-sm">{doc}</span>
                              </div>
                              <Button variant="ghost" size="sm">
                                View
                              </Button>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {viewTenant.notes && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Notes</h4>
                      <div className="bg-muted p-3 rounded-md">
                        <p className="text-sm">{viewTenant.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/dashboard/tenants/${viewTenant.id}/payments`)
                  }
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Payment History
                </Button>
                <Button
                  onClick={() => {
                    setViewTenant(null);
                    handleEditTenant(viewTenant);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Tenant
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
